import { useState } from "react"
import {
  Box,
  Button,
  Flex,
  Select,
  Text,
  Badge,
} from "@radix-ui/themes"
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react"
import { UserWithManagerships, ManageableForAdmin } from "@/types/shared"
import { router } from "@inertiajs/react"

interface ManagershipRowProps {
  user: UserWithManagerships
  assignable: ManageableForAdmin[]
  /** The admin endpoint this row PATCHes, e.g. "/admin/track_managers". */
  endpoint: string
  /** The id param that endpoint expects, e.g. "track_id" or "club_id". */
  paramKey: string
  placeholder: string
  emptyMessage: string
  onUpdate?: () => void
}

export default function ManagershipRow({
  user,
  assignable,
  endpoint,
  paramKey,
  placeholder,
  emptyMessage,
  onUpdate,
}: ManagershipRowProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedId, setSelectedId] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const available = assignable.filter(
    (item) => !user.managed.some((m) => m.id === item.id)
  )

  const submit = async (id: number, action_type: "add" | "remove") => {
    setIsSubmitting(true)
    try {
      await router.visit(endpoint, {
        method: "patch",
        data: {
          user_id: user.id,
          [paramKey]: id,
          action_type,
        },
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          setSelectedId("")
          onUpdate?.()
        },
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedId) return
    await submit(parseInt(selectedId), "add")
  }

  return (
    <Box
      className="border-b py-4"
      style={{ borderColor: "var(--gray-6)" }}
    >
      <Flex justify="between" align="center" gap="4">
        <Flex direction="column" gap="2" style={{ flex: 1 }}>
          <Flex align="center" gap="2">
            <Text weight="medium">{user.email}</Text>
            {user.is_manager && (
              <Badge color="indigo" variant="soft">
                Manager
              </Badge>
            )}
          </Flex>
          {isExpanded && (
            <Flex direction="column" gap="2">
              {user.managed.length > 0 ? (
                <Flex direction="column" gap="2">
                  {user.managed.map((item) => (
                    <Flex
                      key={item.id}
                      justify="between"
                      align="center"
                      gap="2"
                      style={{
                        padding: "8px",
                        backgroundColor: "var(--gray-2)",
                        borderRadius: "4px",
                      }}
                    >
                      <Text size="2">{item.name}</Text>
                      <Button
                        size="1"
                        color="red"
                        variant="ghost"
                        onClick={() => submit(item.id, "remove")}
                        disabled={isSubmitting}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </Flex>
                  ))}
                </Flex>
              ) : (
                <Text size="2" color="gray">
                  {emptyMessage}
                </Text>
              )}

              {available.length > 0 && (
                <form onSubmit={handleAdd}>
                  <Flex gap="2" align="end">
                    <Box style={{ flex: 1 }}>
                      <Select.Root
                        value={selectedId}
                        onValueChange={setSelectedId}
                      >
                        <Select.Trigger placeholder={placeholder} />
                        <Select.Content>
                          <Select.Group>
                            {available.map((item) => (
                              <Select.Item key={item.id} value={item.id.toString()}>
                                {item.name}
                              </Select.Item>
                            ))}
                          </Select.Group>
                        </Select.Content>
                      </Select.Root>
                    </Box>
                    <Button
                      size="1"
                      type="submit"
                      disabled={!selectedId || isSubmitting}
                    >
                      Add
                    </Button>
                  </Flex>
                </form>
              )}
            </Flex>
          )}
        </Flex>
        <Button
          size="1"
          variant="ghost"
          onClick={() => setIsExpanded(!isExpanded)}
          disabled={isSubmitting}
        >
          {isExpanded ? (
            <ChevronUp size={18} />
          ) : (
            <ChevronDown size={18} />
          )}
        </Button>
      </Flex>
    </Box>
  )
}
